import { useState } from "react";
import {
  Button,
  TextField,
  Typography,
  IconButton,
  Box,
  Alert,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Paper,
  Grid,
  Avatar,
  CircularProgress,
  Divider,
} from "@mui/material";
import AddAPhotoIcon from "@mui/icons-material/AddAPhoto";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { useDispatch } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { registerUser } from "../utils/api";
import { loginSuccess, loginFailure } from "../features/authSlice";

const Register = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    username: "",
    email: "",
    password: "",
    gender: "",
    maritalStatus: "",
    image: null,
  });

  const [imagePreview, setImagePreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = "First name is required";
    if (!formData.lastName.trim()) newErrors.lastName = "Last name is required";
    if (!formData.username.trim()) newErrors.username = "Username is required";
    if (!formData.password.trim() || formData.password.length < 6)
      newErrors.password = "Password must be at least 6 characters";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: "" });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, image: file });
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    const formDataToSend = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      if (value) formDataToSend.append(key, value);
    });
    try {
      const { user } = await registerUser(formDataToSend);
      dispatch(loginSuccess(user));
      localStorage.setItem("currentUser", JSON.stringify(user));
      localStorage.setItem("isAuthenticated", "true");
      navigate("/");
    } catch (error) {
      setErrors({ general: error.response?.data?.message || "Something went wrong!" });
      dispatch(loginFailure());
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #1A1A2E 0%, #16213E 55%, #0F3460 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
        py: 4,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 480,
          borderRadius: "20px",
          overflow: "hidden",
          border: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        {/* Brand header */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #1A1A2E 0%, #16213E 100%)",
            py: 3.5,
            px: 4,
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              width: 58,
              height: 58,
              borderRadius: "50%",
              bgcolor: "rgba(212,175,55,0.15)",
              border: "2px solid rgba(212,175,55,0.6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2,
            }}
          >
            <PersonOutlineIcon sx={{ color: "#D4AF37", fontSize: 28 }} />
          </Box>
          <Typography
            sx={{
              fontSize: 28,
              fontWeight: 800,
              background: "linear-gradient(90deg, #fff 10%, #D4AF37 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              letterSpacing: "-0.5px",
            }}
          >
            HasanPulse
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: 13.5, mt: 0.5 }}>
            Create your account
          </Typography>
        </Box>

        {/* Form body */}
        <Box sx={{ px: 4, py: 4, bgcolor: "#fff" }}>
          {errors.general && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: "10px" }}>
              {errors.general}
            </Alert>
          )}

          {/* Profile photo upload */}
          <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
            <Box sx={{ position: "relative" }}>
              <Avatar
                src={imagePreview}
                sx={{
                  width: 80,
                  height: 80,
                  border: "3px solid #E5E7EB",
                  bgcolor: "#F3F4F6",
                  fontSize: 30,
                  color: "#9CA3AF",
                }}
              >
                {!imagePreview && <PersonOutlineIcon sx={{ fontSize: 36 }} />}
              </Avatar>
              <input
                accept="image/*"
                type="file"
                onChange={handleImageChange}
                style={{ display: "none" }}
                id="register-image-upload"
              />
              <label htmlFor="register-image-upload">
                <IconButton
                  component="span"
                  size="small"
                  sx={{
                    position: "absolute",
                    bottom: -2,
                    right: -2,
                    bgcolor: "#1A1A2E",
                    color: "#D4AF37",
                    width: 28,
                    height: 28,
                    border: "2px solid #fff",
                    "&:hover": { bgcolor: "#D4AF37", color: "#1A1A2E" },
                  }}
                >
                  <AddAPhotoIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </label>
            </Box>
          </Box>

          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {/* Name row */}
            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <TextField
                  label="First Name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  error={!!errors.firstName}
                  helperText={errors.firstName}
                  fullWidth
                  required
                  size="medium"
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Last Name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  error={!!errors.lastName}
                  helperText={errors.lastName}
                  fullWidth
                  required
                  sx={fieldSx}
                />
              </Grid>
            </Grid>

            <TextField
              label="Username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              error={!!errors.username}
              helperText={errors.username}
              fullWidth
              required
              sx={fieldSx}
            />
            <TextField
              label="Password"
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              error={!!errors.password}
              helperText={errors.password}
              fullWidth
              required
              sx={fieldSx}
            />
            <TextField
              label="Email (optional)"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              fullWidth
              sx={fieldSx}
            />

            <Divider sx={{ my: 0.5 }}>
              <Typography sx={{ color: "#9CA3AF", fontSize: 12, px: 1 }}>
                Optional details
              </Typography>
            </Divider>

            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <FormControl fullWidth sx={fieldSx}>
                  <InputLabel>Gender</InputLabel>
                  <Select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    label="Gender"
                    sx={{ borderRadius: "10px" }}
                  >
                    <MenuItem value="">
                      <em style={{ color: "#9CA3AF", fontStyle: "normal" }}>Any</em>
                    </MenuItem>
                    <MenuItem value="Male">Male</MenuItem>
                    <MenuItem value="Female">Female</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth sx={fieldSx}>
                  <InputLabel>Marital Status</InputLabel>
                  <Select
                    name="maritalStatus"
                    value={formData.maritalStatus}
                    onChange={handleChange}
                    label="Marital Status"
                    sx={{ borderRadius: "10px" }}
                  >
                    <MenuItem value="">
                      <em style={{ color: "#9CA3AF", fontStyle: "normal" }}>Any</em>
                    </MenuItem>
                    <MenuItem value="Single">Single</MenuItem>
                    <MenuItem value="In a relationship">In a relationship</MenuItem>
                    <MenuItem value="Married">Married</MenuItem>
                    <MenuItem value="Divorced">Divorced</MenuItem>
                    <MenuItem value="Widowed">Widowed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              size="large"
              fullWidth
              sx={{
                mt: 0.5,
                py: 1.4,
                borderRadius: "10px",
                fontWeight: 700,
                fontSize: 15,
                textTransform: "none",
                background: "linear-gradient(135deg, #1A1A2E 0%, #16213E 100%)",
                boxShadow: "0 4px 14px rgba(26,26,46,0.35)",
                "&:hover": {
                  background: "linear-gradient(135deg, #D4AF37 0%, #b8941f 100%)",
                  color: "#1A1A2E",
                  boxShadow: "0 4px 14px rgba(212,175,55,0.4)",
                },
              }}
            >
              {loading ? <CircularProgress size={22} color="inherit" /> : "Create Account"}
            </Button>
          </form>

          <Typography sx={{ mt: 3, textAlign: "center", color: "#6B7280", fontSize: 14 }}>
            Already have an account?{" "}
            <Link
              to="/login"
              style={{ color: "#D4AF37", fontWeight: 700, textDecoration: "none" }}
            >
              Sign in
            </Link>
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    "&:hover fieldset": { borderColor: "#D4AF37" },
    "&.Mui-focused fieldset": { borderColor: "#1A1A2E" },
  },
  "& label.Mui-focused": { color: "#1A1A2E" },
};

export default Register;
